import { useEffect, useState } from "react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from "@/components/ui/card";

import { Badge } from "@/components/ui/badge";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from "@/components/ui/dialog";

import {
  CheckCircle,
  Clock,
  Upload,
  User,
  Mail,
  Phone,
  CalendarDays,
  ShieldCheck,
  Network,
  Hash,
  Zap,
  FileSearch,
  Trash2
} from "lucide-react";

import { toast } from "sonner";

import {
  formatDate,
  formatCurrency
} from "@/utils/constants";

import { getErrorMessage } from "@/utils/errorHelpers";

import { getFileUrl, ordersAPI, partnerAPI } from "@/services/api";
import { LoadingState, PageHeader, PageShell } from "@/components/app/page";
import { ADMIN_RANKS, hasAdminRank, hasFullAdminRank, isCashAccount } from "@/utils/adminRanks";

function DetailItem({ icon: Icon, label, value, mono = false }) {
  return (
    <div className="min-w-0 rounded-lg border border-[#E6E4E0] p-3">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase text-gray-400">
        <Icon size={14} /> {label}
      </div>
      <p className={`mt-1 break-words text-sm text-[#2A4B46] ${mono ? "font-mono" : ""}`}>{value || "-"}</p>
    </div>
  );
}

// =========================================================
// ACTIVATION MANAGEMENT PAGE
// =========================================================

export default function ActivationManagementPage({ user }) {

  const [batches, setBatches] = useState([]);

  const [loading, setLoading] = useState(true);

  const [selectedBatch, setSelectedBatch] = useState(null);

  const [validating, setValidating] = useState(false);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [deletingBatch, setDeletingBatch] = useState(null);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const canUploadProof = isCashAccount(user);
  const canValidateActivations = hasAdminRank(user, ADMIN_RANKS.B);
  const canApproveDeletion = hasFullAdminRank(user);

  const openOperationDetails = (batch, order) => {
    setSelectedOperation({ batch, order });
  };

  // =======================================================
  // LOAD BATCHES
  // =======================================================

  useEffect(() => {
    loadBatches();
  }, []);

  const loadBatches = async () => {

    try {

      // ===============================================
      // BACKEND MUST RETURN BATCHES DIRECTLY
      // ===============================================

      const response =
        await partnerAPI.listActivationBatches();

      setBatches(Array.isArray(response.data) ? response.data : []);

    } catch (error) {

      toast.error(
        "Erreur lors du chargement des lots d'activation"
      );

    } finally {

      setLoading(false);
    }
  };

  // =======================================================
  // VALIDATE BATCH
  // =======================================================

  const handleBatchValidate = async (e) => {

    e.preventDefault();

    if (!selectedBatch) {
      return;
    }

    setValidating(true);

    try {

      // ===============================================
      // SINGLE BATCH VALIDATION
      // ===============================================

      await partnerAPI.validateBatchActivation(selectedBatch.batch_id);

      toast.success(
        `Lot ${selectedBatch.batch_id} validé avec succès`
      );

      setSelectedBatch(null);
      setProofFile(null);

      loadBatches();

    } catch (error) {

      toast.error(
        getErrorMessage(
          error,
          "Erreur lors de la validation du lot"
        )
      );

    } finally {

      setValidating(false);
    }
  };

  const handleUploadProof = async (event) => {
    event.preventDefault();
    if (!selectedBatch || !proofFile) return;

    setUploadingProof(true);
    try {
      await partnerAPI.uploadActivationProof(selectedBatch.batch_id, proofFile);
      toast.success(`Preuve déposée pour le lot ${selectedBatch.batch_id}`);
      setSelectedBatch(null);
      setProofFile(null);
      loadBatches();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors du dépôt de la preuve"));
    } finally {
      setUploadingProof(false);
    }
  };

  const getBatchProofPath = (batch) => {
    const activationOrders = (batch.orders_details || []).filter((order) => order.product_id === "PROD_ACTIVATION");
    const orderWithProof = activationOrders.find((order) => order.payment_proof_path || order.payment?.proof_path);
    return batch.payment_activation?.proof_path
      || batch.proof_path
      || batch.payment?.proof_path
      || orderWithProof?.payment_proof_path
      || orderWithProof?.payment?.proof_path;
  };

  const openBatchProof = (batch) => {
    const proofPath = getBatchProofPath(batch);
    if (!proofPath) {
      toast.error("Aucune preuve disponible");
      return;
    }
    window.open(getFileUrl(proofPath), "_blank");
  };

  const handleDeleteBatch = async (batch) => {
    const hasProof = !!getBatchProofPath(batch);
    const confirmation = hasProof
      ? "Ce lot contient une preuve de paiement. Envoyer une demande de suppression a un administrateur Full ?"
      : "Supprimer definitivement ce lot d'activation sans preuve de paiement ?";
    if (!window.confirm(confirmation)) return;

    setDeletingBatch(batch.batch_id);
    try {
      const response = await ordersAPI.deleteBatchFlow(batch.batch_id, "activation");
      toast.success(response.data?.requires_full_approval ? "Demande de suppression envoyee" : "Lot supprimé");
      loadBatches();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors de la suppression"));
    } finally {
      setDeletingBatch(null);
    }
  };

  const handleApproveDeletion = async (batch) => {
    if (!batch.deletion_request || !window.confirm("Approuver et executer cette suppression ?")) return;
    setDeletingBatch(batch.batch_id);
    try {
      await ordersAPI.approveDeletionRequest(batch.deletion_request.id);
      toast.success("Suppression approuvée et exécutée");
      loadBatches();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors de l'approbation"));
    } finally {
      setDeletingBatch(null);
    }
  };

  // =======================================================
  // LOADING
  // =======================================================

  if (loading) {

    return <LoadingState label="Chargement des sessions d'activation..." />;
  }

  // =======================================================
  // FILTERS
  // =======================================================

  const safeBatches = Array.isArray(batches) ? batches : [];
  const pendingBatches =
    safeBatches.filter(
      (b) => b.status !== "validated" && b.activation_status !== "validated"
    );

  const validatedBatches =
    safeBatches.filter(
      (b) => b.status === "validated" || b.activation_status === "validated"
    );

  // =======================================================
  // VIEW
  // =======================================================

  return (

    <PageShell
      className="mx-auto max-w-6xl"
      data-testid="activation-management-page"
    >
      <PageHeader
        title="Gestion des Activations"
        description="Validation des lots d'activation partenaires"
        actions={
          <Badge variant="outline" className="w-fit px-4 py-1 text-base sm:text-lg">
            {pendingBatches.length} lot(s) en attente
          </Badge>
        }
      />

      <Dialog open={Boolean(selectedOperation)} onOpenChange={(open) => !open && setSelectedOperation(null)}>
        <DialogContent className="max-h-[90dvh] w-[calc(100vw-1rem)] overflow-y-auto rounded-2xl sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#2A4B46]">
              <User size={19} /> Informations du partenaire
            </DialogTitle>
          </DialogHeader>

          {selectedOperation && (() => {
            const partner = selectedOperation.batch?.partner || {};
            const order = selectedOperation.order || {};
            const isValidated = selectedOperation.batch?.status === "validated" || selectedOperation.batch?.activation_status === "validated" || order.status === "validated" || order.status === "completed";
            const photoUrl = partner.profile_photo_path ? getFileUrl(partner.profile_photo_path) : null;
            return (
              <div className="space-y-5 py-2">
                <div className="flex items-center gap-4 rounded-xl bg-[#F9F7F4] p-4">
                  <div className="h-20 w-20 shrink-0 overflow-hidden rounded-full bg-[#E6E4E0]">
                    {photoUrl ? (
                      <img src={photoUrl} alt={`Photo de ${partner.full_name || partner.id}`} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-[#2A4B46]"><User size={30} /></div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-lg font-bold text-[#2A4B46]">{partner.full_name || "Partenaire inconnu"}</p>
                    <p className="font-mono text-sm text-gray-500">{partner.id || selectedOperation.batch?.buyer_id || "-"}</p>
                    <Badge className={isValidated ? "mt-2 bg-green-100 text-green-700 hover:bg-green-100" : "mt-2 bg-amber-100 text-amber-700 hover:bg-amber-100"}>
                      {isValidated ? "Activation validée" : "Activation en attente"}
                    </Badge>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <DetailItem icon={Mail} label="E-mail" value={partner.email} />
                  <DetailItem icon={Phone} label="Téléphone" value={partner.telephone} />
                  <DetailItem icon={CalendarDays} label="Demande initiée le" value={formatDate(order.created_at || selectedOperation.batch?.created_at)} />
                  <DetailItem icon={Hash} label="Opération" value={order.id} mono />
                  <DetailItem icon={Zap} label="Valeur" value={formatCurrency(order.price_mc || 0)} />
                  <DetailItem icon={ShieldCheck} label="KYC" value={partner.is_verified ? "Vérifié" : "Non vérifié"} />
                  <DetailItem icon={Network} label="Parrain direct" value={partner.parrain_name ? `${partner.parrain_name} (${partner.parrain_id})` : partner.parrain_id} />
                  <DetailItem icon={Zap} label="Statut PAE / PAR" value={`${partner.is_active_pae ? "Actif" : "Inactif"} / ${partner.is_active_par ? "Actif" : "Inactif"}`} />
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ================================================= */}
      {/* PENDING */}
      {/* ================================================= */}

      {pendingBatches.length > 0 && (

        <div className="space-y-6 mb-10">

          {pendingBatches.map((batch) => (

            <Card
              key={batch.batch_id || batch.id}
              className="border-none shadow-sm bg-white"
              data-testid={`activation-batch-${batch.batch_id}`}
            >

              <CardContent className="p-6">

                <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-center">

                  {/* =================================== */}
                  {/* LEFT */}
                  {/* =================================== */}

                  <div className="space-y-2">

                    <div className="flex flex-wrap items-center gap-2">

                      <Hash
                        size={16}
                        className="text-[#D4A373]"
                      />

                      <span className="font-mono font-bold text-sm text-gray-500">

                        {batch.batch_id}

                      </span>

                      <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 flex gap-1 items-center">

                        <Clock size={12} />

                        En attente

                      </Badge>

                    </div>

                    <div className="flex items-center gap-2">

                      <User
                        size={20}
                        className="text-[#2A4B46]"
                      />

                      <span className="font-bold text-lg">

                        {batch.buyer_id}

                      </span>

                    </div>

                    <p className="text-sm text-gray-400">

                      {formatDate(batch.created_at)}

                    </p>

                    <div className="mt-3 space-y-1">

                      {(batch.orders_details || []).map((order) => (

                        <div
                          key={order.id || order.order_id || order.product_id}
                          role="button"
                          tabIndex={0}
                          onClick={() => openOperationDetails(batch, order)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              openOperationDetails(batch, order);
                            }
                          }}
                          className="cursor-pointer rounded bg-[#F9F7F4] px-2 py-1 text-xs font-mono text-[#5C625F] transition hover:bg-[#eee9e1] focus:outline-none focus:ring-2 focus:ring-[#D4A373]"
                        >

                          <span className="font-semibold">{order.id}</span>
                          <span className="ml-2 text-[10px] text-gray-400">Voir les informations</span>

                        </div>
                      ))}

                    </div>

                  </div>

                  {/* =================================== */}
                  {/* CENTER */}
                  {/* =================================== */}

                  <div className="grid gap-4 rounded-xl bg-gray-50 p-4 sm:grid-cols-2 lg:flex lg:items-center lg:gap-8">

                    <div className="text-center">

                      <p className="text-xs text-gray-500 uppercase font-semibold">

                        Points

                      </p>

                      <div className="flex items-center gap-1 justify-center text-[#D4A373]">

                        <Zap
                          size={18}
                          fill="currentColor"
                        />

                        <span className="text-2xl font-black">

                          {batch.orders_details?.length || 0}

                        </span>

                      </div>

                    </div>

                    <div className="border-t pt-4 text-left sm:border-l sm:border-t-0 sm:pl-8 sm:pt-0 sm:text-right">

                      <p className="text-xs text-gray-500 uppercase font-semibold">

                        Total Facturé

                      </p>

                      <p className="text-xl font-bold text-[#2A4B46]">

                        {formatCurrency(
                          batch.total_amount_mc || 0
                        )}

                      </p>

                    </div>

                  </div>

                  {/* =================================== */}
                  {/* ACTIONS */}
                  {/* =================================== */}

                  <div className="flex flex-col gap-2 sm:flex-row lg:justify-end">

                  {getBatchProofPath(batch) && (
                    <Button
                      variant="outline"
                      className="flex gap-2"
                      onClick={() => openBatchProof(batch)}
                      data-testid={`activation-proof-${batch.batch_id}`}
                    >
                      <FileSearch size={18} />
                      Voir preuve
                    </Button>
                  )}

                  {canUploadProof && !getBatchProofPath(batch) && (
                  <Dialog onOpenChange={(open) => {
                    if (!open) {
                      setSelectedBatch(null);
                      setProofFile(null);
                    }
                  }}>

                    <DialogTrigger asChild>

                      <Button
                        onClick={() =>
                          {
                            setSelectedBatch(batch);
                            setProofFile(null);
                          }
                        }
                        className="bg-[#2A4B46] hover:bg-[#1a332f] text-white px-8"
                        data-testid={`upload-activation-proof-${batch.batch_id}`}
                      >

                        <Upload
                          size={18}
                          className="mr-2"
                        />

                        Déposer la preuve

                      </Button>

                    </DialogTrigger>

                    <DialogContent className="h-[100dvh] w-screen max-w-none overflow-y-auto rounded-none sm:h-auto sm:max-h-[90vh] sm:max-w-[425px] sm:rounded-2xl">

                      <form onSubmit={handleUploadProof}>

                        <DialogHeader>

                          <DialogTitle>

                            Déposer la preuve de paiement

                          </DialogTitle>

                        </DialogHeader>

                        <div className="grid gap-4 py-4">

                          <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">

                            <p className="text-sm text-amber-800 leading-tight">

                              Cette preuve sera envoyée aux administrateurs habilités à valider :

                              <strong>
                                {" "}
                                {batch.orders_details?.length || 0}
                                {" "}point(s)
                              </strong>

                              {" "}pour :

                              <strong>
                                {" "}
                                {batch.buyer_id}
                              </strong>

                            </p>

                          </div>

                          <div>
                            <Label htmlFor="activation-proof-file">Preuve de paiement Activation</Label>
                            <Input
                              id="activation-proof-file"
                              type="file"
                              accept="image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf"
                              onChange={(event) => setProofFile(event.target.files?.[0] || null)}
                              className="mt-1"
                              data-testid="activation-proof-file"
                              required
                            />
                            <p className="text-xs text-[#5C625F] mt-1">
                              Cette preuve sera rattachée uniquement aux lignes Activation PAR.
                            </p>
                          </div>

                        </div>

                        <DialogFooter>

                          <Button
                            type="submit"
                            className="w-full bg-[#D4A373]"
                            disabled={uploadingProof || !selectedBatch || !proofFile}
                            data-testid="confirm-activation-proof-upload"
                          >

                            {uploadingProof ? "Dépôt..." : "Déposer la preuve"}

                          </Button>

                        </DialogFooter>

                      </form>

                    </DialogContent>

                  </Dialog>
                  )}

                  {canValidateActivations && (
                  <Dialog onOpenChange={(open) => {
                    if (!open) setSelectedBatch(null);
                  }}>
                    <DialogTrigger asChild>
                      <Button
                        onClick={() => setSelectedBatch(batch)}
                        className="bg-[#D4A373] hover:bg-[#b98456] text-white px-8"
                        data-testid={`validate-batch-${batch.batch_id}`}
                      >
                        <CheckCircle size={18} className="mr-2" />
                        valider le paiement
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="h-[100dvh] w-screen max-w-none overflow-y-auto rounded-none sm:h-auto sm:max-h-[90vh] sm:max-w-[425px] sm:rounded-2xl">
                      <form onSubmit={handleBatchValidate}>
                        <DialogHeader><DialogTitle>Validation du Lot d'Activation</DialogTitle></DialogHeader>
                        <div className="grid gap-4 py-4">
                          {!getBatchProofPath(batch) ? (
                            <p className="rounded-lg border border-amber-100 bg-amber-50 p-4 text-sm text-amber-800">Cette activation n'a pas de preuve de paiement. Contactez la caisse.</p>
                          ) : (
                            <Button type="button" variant="outline" className="w-full flex gap-2" onClick={() => openBatchProof(batch)}>
                              <FileSearch size={18} /> Voir la preuve avant validation
                            </Button>
                          )}
                        </div>
                        <DialogFooter>
                          <Button type="submit" className="w-full bg-[#D4A373]" disabled={validating || !selectedBatch} data-testid="confirm-batch-validation">
                            {validating ? "Validation..." : "Confirmer la validation"}
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                  )}

                  {canApproveDeletion && batch.deletion_request && (
                    <Button
                      variant="outline"
                      className="border-red-200 text-red-700 hover:bg-red-50"
                      onClick={() => handleApproveDeletion(batch)}
                      disabled={deletingBatch === batch.batch_id}
                      data-testid={`approve-activation-deletion-${batch.batch_id}`}
                    >
                      <CheckCircle size={18} className="mr-2" />
                      {deletingBatch === batch.batch_id ? "Approbation..." : "Approuver suppression"}
                    </Button>
                  )}

                  {canUploadProof && batch.activation_status !== "validated" && !batch.deletion_request && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-700 hover:bg-red-50 hover:text-red-800"
                      onClick={() => handleDeleteBatch(batch)}
                      disabled={deletingBatch === batch.batch_id}
                      title="Supprimer le lot"
                      aria-label="Supprimer le lot"
                      data-testid={`delete-activation-batch-${batch.batch_id}`}
                    >
                      <Trash2 size={18} />
                    </Button>
                  )}

                  </div>

                </div>

              </CardContent>

            </Card>
          ))}

        </div>
      )}

      {/* ================================================= */}
      {/* VALIDATED */}
      {/* ================================================= */}

      <Card className="border-[#E6E4E0] shadow-sm">

        <CardHeader>

          <CardTitle className="text-xl font-semibold text-[#2A4B46] font-['Outfit']">

            Lots validés ({validatedBatches.length})

          </CardTitle>

        </CardHeader>

        <CardContent>

          {validatedBatches.length === 0 ? (

            <p className="text-center text-[#5C625F] py-8">

              Aucun lot validé

            </p>

          ) : (

            <div className="space-y-3">

              {validatedBatches.map((batch) => (

                <div
                  key={batch.batch_id}
                  className="flex flex-col gap-3 rounded-xl border border-green-200 bg-green-50 p-4 sm:flex-row sm:items-center sm:justify-between"
                >

                  <div
                    className="flex cursor-pointer items-center gap-3 rounded-lg p-1 transition hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-[#D4A373]"
                    role="button"
                    tabIndex={0}
                    onClick={() => openOperationDetails(
                      batch,
                      batch.orders_details?.[0] || {
                        id: batch.batch_id,
                        status: batch.status,
                        created_at: batch.created_at,
                      }
                    )}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openOperationDetails(
                          batch,
                          batch.orders_details?.[0] || {
                            id: batch.batch_id,
                            status: batch.status,
                            created_at: batch.created_at,
                          }
                        );
                      }
                    }}
                  >

                    <CheckCircle
                      size={20}
                      className="text-green-600"
                    />

                    <div>

                      <p className="font-semibold text-[#2A4B46]">

                        {batch.buyer_id}

                      </p>

                      <p className="text-xs text-[#5C625F]">

                        {batch.batch_id}
                        {" • "}
                        {formatDate(batch.created_at)}

                      </p>

                      <div className="mt-2 flex flex-wrap gap-1">
                        {(batch.orders_details || []).map((order) => (
                          <span
                            key={order.id || order.order_id || order.product_id}
                            role="button"
                            tabIndex={0}
                            className="rounded bg-white px-2 py-1 font-mono text-[10px] text-[#5C625F] transition hover:bg-[#F9F7F4] focus:outline-none focus:ring-2 focus:ring-[#D4A373]"
                            onClick={(event) => {
                              event.stopPropagation();
                              openOperationDetails(batch, order);
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                event.stopPropagation();
                                openOperationDetails(batch, order);
                              }
                            }}
                          >
                            {order.id}
                          </span>
                        ))}
                      </div>

                    </div>

                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">

                    <Badge className="bg-green-100 text-green-800">

                      {batch.orders_details?.length || 0}
                      {" "}point(s)

                    </Badge>

                    <Button
  variant="outline"
  className="flex gap-2"
  onClick={() => openBatchProof(batch)}
>

  <FileSearch size={18} />

  Voir preuve

</Button>

                  </div>

                </div>
              ))}

            </div>
          )}

        </CardContent>

      </Card>

    </PageShell>
  );
}
